/* eslint-disable no-mixed-operators */
import * as React from "react";
import { useEffect, useState, useRef } from "react";
import { useSelector, useStore } from "react-redux";
import axios from 'axios';

import {
    Box,
    Button,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    styled,
    TextField,
    Toolbar,
    Typography,
    useTheme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
} from "@mui/material";

import { resetStore, loadStore, setActiveStep as setActiveStepAction } from "./Store/roiStore.ts";

import AddchartIcon from '@mui/icons-material/Addchart';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";


import { ClientsROICalculationList } from "./pages/ClientsROICalculationList.tsx";
import { ROICalculator } from "./pages/ROICalculator.tsx";
import { ROIReportPage } from "./pages/ROIReportPage.tsx";
import { AppLoader } from "./components/AppLoader.tsx";
import { useWizardSteps } from "./hooks/useWizardSteps.tsx";
import { NotFound } from "./pages/NotFound.tsx";
import AssessmentIcon from '@mui/icons-material/Assessment';
import "./styles/app.scss";

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
    open?: boolean, fullwidth?: boolean;
}>(({ theme, open, fullwidth }) => ({
  height: "100%",
    flexGrow: (fullwidth) ? 1 : 0 ,
  padding: theme.spacing(8),
  transition: theme.transitions.create("margin", {
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const RoiCalclatorRouteName = "roiCalculator";
const SavedRoisRouteName = "savedRois";
const ReportRouteName = "report";

function AppContent(props) {
    const [showSavedList, setShowSavedList] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [calculationsList, setCalculationsList] = useState([]);
    const [selectedRoiInfo, setSelectedRoiInfo] = useState();
    
    const [doResetRoi, setDoResetRoi] = useState(false);
    const [showExitRoi, setShowExitRoi] = useState({ exit: false, exitTo: RoiCalclatorRouteName });    

    // this reference intended to access a lower method in ROICalculator.jsx component.
    /* const myRef = useRef();    */

    const { dispatch } = useStore();

    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});

    const state = useSelector((state: any) => state.inputFields);
    const wizardSteps = useWizardSteps();
    const rawActiveStep = state.activeStep ?? 0;
    const activeStep = Math.min(rawActiveStep, Math.max(0, wizardSteps.length - 1));
    const completedSteps = state.completedSteps ?? {};

    useEffect(() => {
        setIsValid(isValidated(activeStep));

        let clientCalculations = [];

        setCalculationsList(clientCalculations);

        if (clientCalculations.length > 0) {
            setShowSavedList(true);
        }

        setIsLoading(false);
    }, [state.client.name]);
   
    const isValidated = (step: number) => {
        const spendDetailRows = state.spendDetailsRows;
        const firstRow = spendDetailRows?.row1 ?? {};
        const facilityName = firstRow["Facility Name"] ?? firstRow.Location ?? "";
        let isValid;
        switch (step) {
            case 0:
                isValid = state.client.name.length > 0;
                break;
            case 1:
                isValid = String(facilityName).length > 0;
                break;
            default:
                isValid = true;
        }
        setIsValid(isValid);
        return isValid;
    };   

    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleShowReport = () => {
        if (state.isDirty || selectedRoiInfo) {
            setShowExitRoi({ exit: true, exitTo: ReportRouteName });
        } else {
            setShowSavedList(false);
            setShowReport(true);
        }
    };

    const handleShowSavedCalculations = () => {
        if (state.isDirty || selectedRoiInfo) {
            setShowExitRoi({ exit: true, exitTo: SavedRoisRouteName });
        } else  {
            onShowSavedRois();
        }
    };

    const onShowSavedRois = () => {
        setSelectedRoiInfo(undefined);
        setDoResetRoi(!doResetRoi);
        setShowSavedList(true);
        setShowReport(false);
    }

    const handleShowRoiCalculator = () => {        
        if (state.isDirty || selectedRoiInfo) {
            setShowExitRoi({ exit: true, exitTo: RoiCalclatorRouteName });
        }
        else {           
            onShowRoiCalculator();
        }
    };

    const onShowRoiCalculator = () => {
        setSelectedRoiInfo(undefined);
        setDoResetRoi(!doResetRoi);
        setShowSavedList(false);
        setShowReport(false);
    };

    const handleCancelRoi = () => {
        setShowExitRoi({ exit: false, exitTo: showExitRoi.exitTo });
    };

    const handleDiscardRoi = (obj) => {        
        if (showExitRoi.exitTo === RoiCalclatorRouteName) {
            onShowRoiCalculator();
        }
        else if (showExitRoi.exitTo === SavedRoisRouteName) {
            onShowSavedRois();
        }
        else if (showExitRoi.exitTo === ReportRouteName) {
            setShowSavedList(false);
            setShowReport(true);
        }

        dispatch(resetStore({})); 

        setShowExitRoi({ exit: false, exitTo: showExitRoi.exitTo });
    };

    const handleSignOut = () => {
        window.location.href = window.location.origin + "/SignOut";
    };

    //const handleSaveRoi = (obj) => {
    //    if (myRef.current) {
    //        let ignoreSuccess = true;
    //        myRef.current.handleParentSave(ignoreSuccess);            
    //        setShowConfirmNewRoi(false);
    //        setSelectedRoiInfo(undefined);
    //        setShowSavedList(false);
    //    }
    //}

    const getLoaderContent = () => {
        return (
            <AppLoader />);
    };

    const handleSavedRoiItemEditClick = (roiInfo) => {
        setSelectedRoiInfo(roiInfo);
        setShowSavedList(false);
    }

    const getContent = () => {
        if (showReport) {
            return <ROIReportPage />;
        }

        if (showSavedList) {
            return (
                <ClientsROICalculationList onItemEditClick={handleSavedRoiItemEditClick} />
            );
        }

        return (
            <ROICalculator /*ref={myRef}*/ doReset={doResetRoi} roiInfo={selectedRoiInfo}
                onOpenSavedRois={() => { setShowSavedList(true); }}
                onRestSelectedRoi={() => { setSelectedRoiInfo(undefined); }}
                userName={props.userName}
                userEmail={props.userEmail} />
        );
    }

    return (
        <div className="App">
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Toolbar style={{ background: "#003F81" }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: "none" }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h5" noWrap component="div">
                        PAR Excellence ROI Calculator
                    </Typography>
                </Toolbar>
            </AppBar>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        boxSizing: "border-box",
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === "ltr" ? (
                            <ChevronLeftIcon />
                        ) : (
                            <ChevronRightIcon />
                        )}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    <ListItem key={"ROI Calculator"} disablePadding>
                        <ListItemButton
                            onClick={handleShowRoiCalculator}
                            selected={!showSavedList}
                        >
                            <ListItemIcon>
                                <AddchartIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={"ROI Calculator"}
                                primaryTypographyProps={{ fontWeight: 600 }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Box
                    sx={{
                        pl: 3,
                        pr: 2,
                        py: 1,
                        opacity: (showSavedList || showReport) ? 0.55 : 1,
                        transition: "opacity 150ms ease",
                    }}
                >
                    <Stepper
                        nonLinear
                        activeStep={(showSavedList || showReport) ? -1 : activeStep}
                        orientation="vertical"
                        sx={{
                            "& .MuiStepLabel-label": {
                                fontSize: "0.875rem",
                                lineHeight: 1.25,
                                fontWeight: 500,
                            },
                            "& .MuiStepLabel-label.Mui-active": {
                                fontWeight: 500,
                                color: "primary.main",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                                fontWeight: 500,
                                color: "text.primary",
                            },
                            "& .MuiStepConnector-line": {
                                minHeight: 14,
                            },
                        }}
                    >
                        {wizardSteps.map((step, index) => (
                            <Step key={step.key} completed={!!completedSteps[index]}>
                                <StepButton
                                    onClick={() => {
                                        if (showSavedList) setShowSavedList(false);
                                        dispatch(setActiveStepAction(index));
                                    }}
                                    sx={{
                                        textAlign: "left",
                                        py: 0.25,
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                            borderRadius: 1,
                                        },
                                    }}
                                >
                                    <StepLabel>{step.name}</StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
                <Divider sx={{ my: 1 }} />
                <List>
                    <ListItem key={"Saved ROIs"} disablePadding>
                        <ListItemButton
                            onClick={handleShowSavedCalculations}
                            selected={showSavedList}
                        >
                            <ListItemIcon>
                                <FolderOpenIcon />
                            </ListItemIcon>
                            <ListItemText primary={"Saved ROIs"} />
                        </ListItemButton>
                    </ListItem>
                    {props.isReportingUser && (
                        <ListItem key={"ROI Report"} disablePadding>
                            <ListItemButton
                                onClick={handleShowReport}
                                selected={showReport}
                            >
                                <ListItemIcon>
                                    <AssessmentIcon />
                                </ListItemIcon>
                                <ListItemText primary={"ROI Report"} />
                            </ListItemButton>
                        </ListItem>
                    )}
                </List>
                <Divider />
                <Box sx={{ position: 'absolute', bottom: 0, width: 1 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        p: 1,
                        m: 1,
                    }}>
                        <Box sx={{
                            p: 0,
                            m: 0,
                        }}>
                            {(props.environment.toLowerCase().indexOf('dev') >= 0) ?
                                <>
                                    <Typography variant="caption" display="block" gutterBottom>
                                        Environment: {props.environment}
                                    </Typography>
                                    <Typography variant="caption" display="block" gutterBottom>
                                        Region: {props.region}
                                    </Typography>
                                </>
                                : ""}
                            <Typography variant="caption" display="block" gutterBottom>
                                Version: {props.appVersion}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        
                        <Button variant="contained" sx={{ margin: "10px" }} onClick={() => handleSignOut()}>
                            Sign out
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Main open={open} fullwidth={+!showSavedList}>
                <DrawerHeader />
                {/*
                Check the status of the 'loading' variable. If true, then display
                the loading spinner. Otherwise, display the results.
                */}

                {isLoading ? getLoaderContent() : getContent()}

            </Main>            
            {showExitRoi.exit  && <Dialog
                open={showExitRoi.exit}
                onClose={handleCancelRoi}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Alert severity="warning">
                        Are you sure you want to leave this section?
                    </Alert>
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 0 }} >
                    <DialogContentText id="alert-dialog-description">                        
                        <Box sx={{ mt: 0, mb: 0, width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                            If you have unsaved changes, these changes will not be saved automatically and will be lost unless you save it. Do you want to leave without saving?                           
                        </Box>                        
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ mt: 0, width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={handleDiscardRoi}>Yes, Leave Without Saving</Button>
                        <Button onClick={handleCancelRoi}>Cancel</Button>
                    </Box>
                </DialogActions>
            </Dialog>}
        </div>
    );
}

export default AppContent;
